import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin, Clock, ArrowRight } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { SITE, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";

const ADDRESS = "10335 Reserve Dr, San Diego, CA 92127";
const MAP_SRC = "https://www.google.com/maps?q=10335%20Reserve%20Dr%2C%20San%20Diego%2C%20CA%2092127&output=embed";

export default function Contact() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const canonical = `${SITE}/contact`;
  const title = "Contact CleanOut247 | Junk Removal in San Diego County, CA";
  const description = `Contact CleanOut247 for fast, upfront-priced junk removal across San Diego County. Call or text ${PHONE_DISPLAY}, email office@cleanout247.com, or visit us at ${ADDRESS}.`;

  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "MovingCompany",
    name: "CleanOut247",
    url: SITE,
    telephone: "+1-619-999-3565",
    email: "office@cleanout247.com",
    image: `${SITE}/og-logo.png`,
    address: { "@type": "PostalAddress", streetAddress: "10335 Reserve Dr", addressLocality: "San Diego", addressRegion: "CA", postalCode: "92127", addressCountry: "US" },
    geo: { "@type": "GeoCoordinates", latitude: 33.0208, longitude: -117.1165 },
    areaServed: { "@type": "AdministrativeArea", name: "San Diego County, CA" },
  }];

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="contact-page">
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <Header />

      <section className="pt-28 sm:pt-40 lg:pt-48 pb-14 px-5 lg:px-8 bg-[#0B1E45] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
            <span className="h-px w-8 bg-[#5DB82F]" /> Get In Touch
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl">Contact CleanOut247</h1>
          <p className="text-white/70 mt-5 text-lg leading-relaxed max-w-2xl">
            Call, text, or email for a fast, free quote — same-day junk removal across San Diego County, 7 days a week.
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
            <h2 className="font-display font-black tracking-tighter text-3xl text-[#15306E] mb-6">CleanOut247 Junk Removal</h2>
            <ul className="space-y-5">
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <span className="bg-[#EAF4E3] text-[#46A01E] rounded-sm p-2.5 shrink-0"><Phone size={22} weight="fill" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#71717A] font-bold">Call / Text</div>
                  <a href={`tel:${PHONE_TEL}`} className="font-display font-extrabold text-lg text-[#15306E] hover:text-[#46A01E] transition-colors">{PHONE_DISPLAY}</a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-email">
                <span className="bg-[#EAF4E3] text-[#46A01E] rounded-sm p-2.5 shrink-0"><EnvelopeSimple size={22} weight="fill" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#71717A] font-bold">Email</div>
                  <a href="mailto:office@cleanout247.com" className="font-display font-extrabold text-lg text-[#15306E] hover:text-[#46A01E] transition-colors">office@cleanout247.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-address">
                <span className="bg-[#EAF4E3] text-[#46A01E] rounded-sm p-2.5 shrink-0"><MapPin size={22} weight="fill" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#71717A] font-bold">Location</div>
                  <p className="font-display font-extrabold text-lg text-[#15306E]">{ADDRESS}</p>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-hours">
                <span className="bg-[#EAF4E3] text-[#46A01E] rounded-sm p-2.5 shrink-0"><Clock size={22} weight="fill" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#71717A] font-bold">Hours</div>
                  <p className="font-display font-extrabold text-lg text-[#15306E]">Open 24/7 · Same-day service</p>
                </div>
              </li>
            </ul>

            <div className="mt-7 bg-[#F4F4F5] rounded-sm p-5">
              <h3 className="font-display font-bold text-[#15306E] mb-1">Service Area</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                Proudly serving San Diego County including San Diego, Rancho Bernardo, 4S Ranch, Rancho Santa Fe,
                Del Mar, Solana Beach, Encinitas, Carlsbad, Oceanside, Escondido, Poway, La Jolla and surrounding communities.{" "}
                <Link to="/service-areas" className="text-[#46A01E] font-semibold hover:underline">See all service areas →</Link>
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-call-btn" className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors">
                <Phone size={18} weight="fill" /> Call/Text Now
              </a>
              <Link to="/#estimate" data-testid="contact-estimate-btn" className="flex items-center gap-2 border border-[#E4E4E7] hover:border-[#46A01E] text-[#15306E] font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors">
                Get Instant Estimate <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>

          <div className="rounded-sm overflow-hidden border border-[#E4E4E7] shadow-sm">
            <iframe
              title="CleanOut247 location map"
              data-testid="contact-map"
              src={MAP_SRC}
              width="100%"
              height="520"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
